import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export const askQuestions = async (brief) => (await api.post("/scripts/questions", brief, { timeout: 120000 })).data;

export const QuestionList = ({ questions, answers, setAnswers }) => (
  <div className="space-y-3" data-testid="ai-questions">
    <p className="text-xs text-slate-600 flex items-center gap-1"><MessageCircleQuestion className="h-3.5 w-3.5" />A few questions so the AI gets this call exactly right (skip any you like):</p>
    {questions.map((q, i) => (
      <label key={q} className="block space-y-1">
        <span className="text-xs font-medium text-slate-800">{q}</span>
        <Textarea rows={2} data-testid={`ai-question-answer-${i}`} value={answers[i] || ""} onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })} />
      </label>
    ))}
  </div>
);

export const toQA = (questions, answers) => questions.map((q, i) => ({ q, a: answers[i] || "" })).filter((x) => x.a.trim());

// Dialer: interview inside "Describe this call"; answers are merged into the call's details.
export const AskMeButton = ({ brief, onAnswers }) => {
  const [qs, setQs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    try {
      const d = await askQuestions(brief);
      if (d.ready) toast.success("Nothing missing — the AI has what it needs");
      else { setQs(d.questions); setAnswers({}); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (qs) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-3">
        <QuestionList questions={qs} answers={answers} setAnswers={setAnswers} />
        <div className="flex gap-2">
          <Button size="sm" data-testid="ai-questions-apply-btn" onClick={() => { onAnswers(toQA(qs, answers)); setQs(null); toast.success("Added to this call"); }}>Add to this call</Button>
          <Button size="sm" variant="ghost" data-testid="ai-questions-cancel-btn" onClick={() => setQs(null)}>Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <Button size="sm" variant="outline" className="w-full" data-testid="ask-me-questions-btn" disabled={busy || !(brief.purpose || brief.goal)} onClick={ask}>
      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MessageCircleQuestion className="h-4 w-4 mr-1" />}
      {busy ? "Thinking of questions…" : "Ask me questions to complete this call"}
    </Button>
  );
};
